import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { isApiError } from '@/api/errors';
import { releaseQueue } from '@/api/ops';
import type { QueueView } from '@/api/types';
import { useCan } from '@/auth/session';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

export function QueuesSection({ queues, staleAfter }: { queues: QueueView[]; staleAfter: string }) {
  const [message, setMessage] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>Queues</CardTitle>
        <CardDescription>
          Work claimed for longer than {staleAfter} counts as stale. Releasing hands it back to the
          queue.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {message && (
          <Alert>
            <AlertDescription>{message}</AlertDescription>
          </Alert>
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          {queues.map((queue) => (
            <QueueCard key={queue.slug} queue={queue} onReleased={setMessage} />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function QueueCard({
  queue,
  onReleased,
}: {
  queue: QueueView;
  onReleased: (message: string) => void;
}) {
  const can = useCan();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const release = useMutation({
    mutationFn: () => releaseQueue(queue.slug),
    onSuccess: (result) => {
      onReleased(`${result.label}: released ${result.released} stale item(s).`);
      void queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      setOpen(false);
    },
  });

  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-center justify-between">
        <span className="font-medium">{queue.label}</span>
        <Badge variant={queue.healthy ? 'secondary' : 'destructive'}>
          {queue.healthy ? 'Healthy' : 'Stale work'}
        </Badge>
      </div>
      <dl className="grid grid-cols-3 gap-2 text-sm text-muted-foreground">
        <div>
          <dt>Due</dt>
          <dd className="text-foreground">{queue.due}</dd>
        </div>
        <div>
          <dt>In progress</dt>
          <dd className="text-foreground">{queue.in_progress}</dd>
        </div>
        <div>
          <dt>Stale</dt>
          <dd className="text-foreground">{queue.stale}</dd>
        </div>
      </dl>
      {can('maintenance.operate') && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={queue.stale === 0}
              aria-label={`Release stale ${queue.label}`}
            >
              Release stale
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Release stale {queue.label.toLowerCase()}?</DialogTitle>
              <DialogDescription>{queue.release_risk}</DialogDescription>
            </DialogHeader>
            {release.isError && (
              <Alert variant="destructive">
                <AlertDescription>
                  {isApiError(release.error) ? release.error.message : 'Release failed.'}
                </AlertDescription>
              </Alert>
            )}
            <DialogFooter>
              <Button onClick={() => release.mutate()} disabled={release.isPending}>
                {release.isPending ? 'Releasing…' : `Release ${queue.stale}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
